import { useZonesPerStatusCountQuery } from '@/__generated__/graphql';
import * as state from '@/state';
import { primary, WorkflowStageColors } from '@/theme/colors';
import { Stack, Typography } from '@mui/material';
import { useMemo } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { StackedBarChart } from './StackedBarChart';
import { CustomSkeleton, StackSkeleton, Widget } from './styles';
import { FINAL_WORKFLOW_STAGE } from '@/constants';
import { useGetStructureId } from '@/hooks/useGetStructureId';

const Skeleton = () => {
  const skeletonWidths = ['4.6rem', '11.4rem', '9.2rem', '13rem', '8rem'];
  return (
    <Stack gap="1.6rem">
      <StackSkeleton animation="wave" />
      <Stack direction="row" gap="2.2rem">
        {skeletonWidths.map((width, index) => (
          <CustomSkeleton key={index} animation="wave" width={width} />
        ))}
      </Stack>
    </Stack>
  );
};

export const StatusWidget = () => {
  const [zonesPerStatusCount, setZoneStatusCounts] = useAtom(state.zoneStatusCounts);
  const stages = useAtomValue(state.workflowStages);
  const searchTerm = useAtomValue(state.searchedZone);
  const structureId = useGetStructureId();

  const { loading } = useZonesPerStatusCountQuery({
    variables: {
      structureId,
      searchTerm: searchTerm,
    },
    skip: !structureId,
    onCompleted: (data) => {
      if (data.zonesPerStatusCount && stages) {
        const sortedZonesPerStatusCount = [...data.zonesPerStatusCount].sort((a, b) => {
          const aOrder =
            stages.find((stage) => stage.name === a.status)?.orderInWorkflow ?? Infinity;
          const bOrder =
            stages.find((stage) => stage.name === b.status)?.orderInWorkflow ?? Infinity;
          return aOrder - bOrder;
        });
        setZoneStatusCounts(sortedZonesPerStatusCount);
      }
    },
  });

  const progressBarData = useMemo(() => {
    if (!zonesPerStatusCount) return [];
    return zonesPerStatusCount.map((item) => ({
      name: item.status,
      count: item.count,
    }));
  }, [zonesPerStatusCount]);

  const displayConfig = useMemo(() => {
    if (!zonesPerStatusCount) return [];

    return zonesPerStatusCount.map((workStage, index) => ({
      value: workStage.status,
      color:
        workStage.status === FINAL_WORKFLOW_STAGE
          ? WorkflowStageColors.at(-1)?.fill ?? WorkflowStageColors[0].fill
          : WorkflowStageColors[index].fill ?? WorkflowStageColors[0].fill,
    }));
  }, [zonesPerStatusCount]);

  return (
    <Widget>
      <Typography
        fontSize="1.6rem"
        fontWeight={500}
        lineHeight="100%"
        color={primary.independence}
        marginBottom="2.2rem"
      >
        Status
      </Typography>
      {loading ? (
        <Skeleton />
      ) : (
        <StackedBarChart data={progressBarData} displayConfig={displayConfig} />
      )}
    </Widget>
  );
};
